import React, { useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Button,
  Backdrop,
  CircularProgress,
  TextField,
  FormHelperText,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import TopNavBar from '../../Components/TopNavigation/TopNavBar';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/rootReducers';
import { AppDispatch } from '../../store';
import { setUser } from '../../store/slices/User';

import { request } from '../../Utils/request';




const DeactivateAccount: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.user);
  const userData = user?.user;
  const isLoggedIn = !!userData;
  const deactivationRequest = userData?.deactivationRequest;
  const status = deactivationRequest?.status;
  const isRequested = deactivationRequest?.requested === true;
  const isApproved = status === 'approved';
  const isPending = isRequested && status === 'pending';
  const isNotRequested = !isRequested;
  const [reason, setReason] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);


  const handleSubmitDeactivationRequest = async () => {
    const reasonTrimmed = reason.trim();
    if (!reasonTrimmed) {
      setSubmitError('Please enter a reason for deactivation.');
      return;
    }
    setSubmitError(null);
    setSubmitLoading(true);
    try {
      await request<{ success?: boolean; message?: string }>('/profile/request-deactivation', {
        method: 'POST',
        body: { reason: reasonTrimmed },
        authorization: true,
      });
      window.alert('Your deactivation request has been submitted. Our team will process it shortly.');
      navigate(-1);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to submit deactivation request. Please try again or contact Admin.';
      setSubmitError(message);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCancelDeactivationRequest = async () => {
    setSubmitError(null);
    setSubmitLoading(true);
    try {
      await request<{ success?: boolean; message?: string; data?: any }>('/profile/cancel-deactivation', {
        method: 'DELETE',
        authorization: true,
      });
      if (userData) {
        dispatch(setUser({ ...userData, isDeactivated: false }));
      }
      window.alert('Your deactivation request has been cancelled.');
      navigate(-1);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to cancel deactivation request. Please try again.';
      setSubmitError(message);
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <TopNavBar title="Request Account deactivation" canGoBack />

      <Container maxWidth="sm" sx={{ flex: 1, py: 3, px: 2 }}>
        <Box sx={{ py: 2 }}>
          {!isLoggedIn && (
            <>
              <Typography
                variant="body1"
                sx={{
                  color: '#49454F',
                  mb: 2,
                  lineHeight: 1.6,
                  fontWeight: 500,
                }}
              >
                If you want to delete your Account from IFinds, please follow these steps:
              </Typography>
              <Typography
                variant="body2"
                component="ol"
                sx={{
                  color: '#49454F',
                  mb: 3,
                  lineHeight: 1.8,
                  pl: 2.5,
                  '& li': { mb: 1 },
                }}
              >
                <li>Log in to your IFinds account</li>
                <li>Go to Account Settings</li>
                <li>Click on &quot;Deactivate Account&quot;</li>
              </Typography>
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate('/login', { state: { from: '/deactivate-account' } })}
                sx={{
                  bgcolor: '#008387',
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#006d70' },
                }}
              >
                Log in
              </Button>
            </>
          )}

          {isLoggedIn && isNotRequested && (
            <>
              <Typography
                variant="body2"
                sx={{
                  color: '#49454F',
                  mb: 3,
                  lineHeight: 1.6,
                }}
              >
                Request to deactivate your account. Please tell us why you want to deactivate. Your request will be
                reviewed and processed accordingly.
              </Typography>

              <TextField
                fullWidth
                label="Reason for deactivation"
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  setSubmitError(null);
                }}
                placeholder="Enter your reason"
                required
                error={!!submitError && !reason.trim()}
                helperText={submitError && !reason.trim() ? submitError : undefined}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': { borderRadius: 2 },
                }}
              />

              {submitError && reason.trim() && (
                <FormHelperText error sx={{ mb: 1 }}>
                  {submitError}
                </FormHelperText>
              )}

              <Button
                variant="contained"
                fullWidth
                onClick={handleSubmitDeactivationRequest}
                disabled={submitLoading || !reason.trim()}
                sx={{
                  bgcolor: 'red',
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  mb: 2,
                  '&:hover': { bgcolor: 'red' },
                  '&.Mui-disabled': { bgcolor: 'red', color: '#fff' },
                }}
              >
                Submit deactivation request
              </Button>
            </>
          )}

          {isLoggedIn && isPending && (
            <>
              <Typography
                variant="body2"
                sx={{
                  color: '#49454F',
                  mb: 3,
                  lineHeight: 1.6,
                }}
              >
                Your deactivation request is under review. Our team will process it shortly.
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#49454F',
                  mb: 3,
                  lineHeight: 1.6,
                }}
              >
                Change your mind? You can cancel your request here.
              </Typography>
              {submitError && (
                <FormHelperText error sx={{ mb: 1 }}>
                  {submitError}
                </FormHelperText>
              )}
              <Button
                variant="outlined"
                fullWidth
                onClick={handleCancelDeactivationRequest}
                disabled={submitLoading}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  mb: 2,
                  borderColor: '#008387',
                  color: '#008387',
                  '&:hover': { borderColor: '#006d70', color: '#006d70', bgcolor: 'rgba(0, 131, 135, 0.04)' },
                }}
              >
                Cancel deactivation request
              </Button>
            </>
          )}

          {isLoggedIn && isApproved && (
            <>
              <Typography
                variant="body2"
                sx={{
                  color: '#49454F',
                  mb: 3,
                  lineHeight: 1.6,
                }}
              >
                Your account deactivation request has been approved. Your account has been or will be deactivated shortly.
              </Typography>
            </>
          )}

        </Box>
      </Container>

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={ submitLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default DeactivateAccount;
